import {
  useContext,
  useLayoutEffect,
  useState,
  type ComponentProps,
} from "react";
import type { SessionMarkdown as SessionMarkdownComponent } from "./SessionMarkdown";
import { TranscriptLoadGateContext } from "./transcriptLoadGate";

type LoadedSessionMarkdown = typeof SessionMarkdownComponent;

let loaded: LoadedSessionMarkdown | null = null;
let loading: Promise<LoadedSessionMarkdown> | null = null;

/** Starts loading the markdown chunk; a SessionMarkdown rendered after it lands renders in its parent's commit. */
export function preloadSessionMarkdown(): Promise<LoadedSessionMarkdown> {
  loading ??= import("./SessionMarkdown").then(
    (m) => (loaded = m.SessionMarkdown),
    (cause: unknown) => {
      loading = null;
      throw cause;
    },
  );
  return loading;
}

/** For a caller whose own chunk already includes SessionMarkdown. */
export function provideSessionMarkdown(component: LoadedSessionMarkdown): void {
  loaded = component;
  loading ??= Promise.resolve(component);
}

/**
 * SessionMarkdown in its own chunk. `next/dynamic` suspends on every first
 * render, so even with the chunk loaded its content mounts a commit after
 * the transcript; this renders synchronously once the chunk has loaded and
 * holds the transcript load gate until then.
 */
export function SessionMarkdown(props: ComponentProps<LoadedSessionMarkdown>) {
  const gate = useContext(TranscriptLoadGateContext);
  const [Markdown, setMarkdown] = useState(() => loaded);
  const [failure, setFailure] = useState<{ cause: unknown } | null>(null);
  useLayoutEffect(() => {
    if (Markdown) return undefined;
    const release = gate?.hold("chunk");
    let mounted = true;
    preloadSessionMarkdown().then(
      (component) => {
        if (mounted) setMarkdown(() => component);
      },
      (cause: unknown) => {
        if (mounted) setFailure({ cause });
      },
    );
    return () => {
      mounted = false;
      release?.();
    };
  }, [Markdown, gate]);
  if (failure) throw failure.cause;
  return Markdown ? <Markdown {...props} /> : null;
}
